import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, CheckCircle2, Ban, CalendarClock } from 'lucide-react';
import type { CobrancaAcaoEtapa, CobrancaAcaoCanal } from '../../../types';
import { CANAIS, RESULTADOS_CONTATO, ETAPAS_MANUAIS, formatBRL, formatDia } from './etapas';
import { propsFundoModal } from '../../../src/lib/backdropModal';
import DesfechoContatoCampos from '../../../src/components/DesfechoContatoCampos';
import { desfechoInicial, faltaNoDesfecho, type CamposDesfecho } from '../../../src/lib/desfechoContato';

const isoLocal = (d: Date) => {
    const c = new Date(d);
    c.setMinutes(c.getMinutes() - c.getTimezoneOffset());
    return c.toISOString().slice(0, 10);
};
const hojeISO = () => isoLocal(new Date());
const amanhaISO = () => isoLocal(new Date(Date.now() + 86_400_000));

/**
 * 'contato'     → atendimento do cliente (D+4, D+10, retorno agendado)
 * 'conferencia' → D+24: conferiu o pagamento, registra o que viu
 * 'dispensar'   → tira as etapas da fila com um motivo
 */
export type ModoRegistro = 'contato' | 'conferencia' | 'dispensar';

/** O que o modal mostra sobre o cliente — um card pode juntar várias etapas. */
export interface AlvoRegistro {
    cliente: string;
    etapas: CobrancaAcaoEtapa[];
    valor: number | null;
    /** Vencimento mais antigo das etapas. */
    vencimento: string | null;
    /** Próximo contato que já estava agendado, para não apagar sem querer. */
    proximoAtual?: string | null;
    /**
     * Sem CPF/CNPJ não existe carteira do cliente — registra só nas etapas,
     * sem agendar retorno nem mudar status.
     */
    semCpf?: boolean;
}

export interface DadosRegistro {
    canal: CobrancaAcaoCanal;
    /** 'YYYY-MM-DD', nunca no futuro. */
    data: string;
    resultado: string;
    observacao: string;
    /** Como ficou o cliente (#6165). Ignorado sem CPF e na conferência. */
    desfecho: CamposDesfecho;
}

interface Props {
    alvo: AlvoRegistro | null;
    modo: ModoRegistro;
    onClose: () => void;
    onConfirmarRegistro: (dados: DadosRegistro) => Promise<void>;
    onConfirmarDispensa: (motivo: string) => Promise<void>;
}

const TITULO: Record<ModoRegistro, string> = {
    contato: 'Registrar contato',
    conferencia: 'Registrar conferência',
    dispensar: 'Dispensar',
};

/**
 * Registro do atendimento de um cliente no Painel de Cobrança.
 *
 * Pedido do #6147: registrar separado a data do contato, a data do próximo
 * contato e a observação. Com o próximo contato preenchido, o cliente sai do
 * painel até a data e volta sozinho nela — não aparece mais todo dia.
 *
 * #6165: o contato não fica mais sem próximo passo — ou data de retorno, ou
 * status (negociando, acordo fechado, pago). Era assim que o cliente sumia do
 * painel sem ninguém voltar a falar com ele.
 *
 * A observação continua obrigatória: sem ela o registro não serve para o
 * dashboard de eficiência que a Meryene pediu, vira só um carimbo.
 */
const RegistrarAcaoModal: React.FC<Props> = ({ alvo, modo, onClose, onConfirmarRegistro, onConfirmarDispensa }) => {
    const [canal, setCanal] = useState<CobrancaAcaoCanal>('ligacao');
    const [data, setData] = useState(hojeISO());
    const [resultado, setResultado] = useState('');
    const [desfecho, setDesfecho] = useState<CamposDesfecho>(() => desfechoInicial(null, hojeISO()));
    const [observacao, setObservacao] = useState('');
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);

    // Cada abertura começa limpa — senão o texto do cliente anterior vai junto.
    const chaveAlvo = alvo ? `${alvo.cliente}|${alvo.etapas.join(',')}|${modo}` : '';
    useEffect(() => {
        if (!alvo) return;
        setCanal('ligacao');
        setData(hojeISO());
        setResultado('');
        // Retorno já agendado e ainda no futuro vem preenchido: registrar um
        // contato sem mexer nele não deve apagá-lo.
        setDesfecho(desfechoInicial(alvo.proximoAtual, hojeISO()));
        setObservacao('');
        setMotivo('');
    }, [chaveAlvo]);

    if (!alvo) return null;

    const registrando = modo !== 'dispensar';
    const ehContato = modo === 'contato';
    // Sem CPF não há carteira: o contato fica só na etapa, sem retorno nem status.
    const pedeDesfecho = ehContato && !alvo.semCpf;
    const podeConfirmar = registrando
        ? observacao.trim().length > 0 && (!pedeDesfecho || !faltaNoDesfecho(desfecho, hojeISO()))
        : motivo.trim().length > 0;

    const confirmar = async () => {
        if (!podeConfirmar) return;
        setSalvando(true);
        try {
            if (registrando) await onConfirmarRegistro({ canal, data, resultado, observacao, desfecho });
            else await onConfirmarDispensa(motivo);
        } finally {
            setSalvando(false);
        }
    };

    const inputCls = 'w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50';
    const labelCls = 'block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5';

    return createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border dark:border-gray-700 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between p-5 border-b border-gray-100 dark:border-gray-700">
                    <div>
                        <div className="flex flex-wrap gap-1.5">
                            {alvo.etapas.map(etapa => (
                                <span key={etapa} className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${ETAPAS_MANUAIS[etapa].badge}`}>
                                    {etapa} · {ETAPAS_MANUAIS[etapa].titulo}
                                </span>
                            ))}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mt-2">{TITULO[modo]}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {alvo.cliente}
                            {alvo.valor ? ` · ${formatBRL(alvo.valor)}` : ''}
                            {alvo.vencimento ? ` · venc. ${formatDia(alvo.vencimento)}` : ''}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {registrando ? (
                        <>
                            {modo === 'conferencia' ? (
                                <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
                                    {ETAPAS_MANUAIS['D+24'].instrucao}
                                </p>
                            ) : null}

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Como falou</label>
                                    <select value={canal} onChange={e => setCanal(e.target.value as CobrancaAcaoCanal)} className={inputCls}>
                                        {CANAIS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>{ehContato ? 'Data do contato' : 'Quando'}</label>
                                    <input
                                        type="date"
                                        value={data}
                                        max={hojeISO()}
                                        onChange={e => setData(e.target.value)}
                                        className={inputCls}
                                    />
                                </div>
                            </div>

                            {ehContato ? (
                                <>
                                    <div>
                                        <label className={labelCls}>Resultado</label>
                                        <select value={resultado} onChange={e => setResultado(e.target.value)} className={inputCls}>
                                            <option value="">—</option>
                                            {RESULTADOS_CONTATO.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                        </select>
                                    </div>

                                    {pedeDesfecho ? (
                                        <DesfechoContatoCampos
                                            valor={desfecho}
                                            onChange={setDesfecho}
                                            hoje={hojeISO()}
                                            amanha={amanhaISO()}
                                            inputClassName={inputCls}
                                            labelClassName={labelCls}
                                        />
                                    ) : (
                                        <p className="flex items-start gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 -mt-1">
                                            <CalendarClock size={13} className="shrink-0 mt-px" />
                                            Este cliente não tem CPF/CNPJ cadastrado na UC, então não dá para agendar o retorno. O contato fica registrado na etapa.
                                        </p>
                                    )}
                                </>
                            ) : null}

                            <div>
                                <label className={labelCls}>
                                    {ehContato ? 'Observações do atendimento' : 'O que foi conferido'} <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows={4}
                                    value={observacao}
                                    onChange={e => setObservacao(e.target.value)}
                                    placeholder={ehContato
                                        ? 'Ex.: falei com o titular, disse que paga sexta. Combinei retorno na segunda.'
                                        : 'Ex.: pagamento não entrou, cliente segue sem retorno.'}
                                    className={`${inputCls} resize-none`}
                                />
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                    Obrigatório — é daqui que sai o histórico de cobrança do cliente.
                                </p>
                            </div>
                        </>
                    ) : (
                        <div>
                            <label className={labelCls}>
                                Por que não vai fazer {alvo.etapas.length > 1 ? 'estas etapas' : 'esta etapa'}? <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                rows={4}
                                value={motivo}
                                onChange={e => setMotivo(e.target.value)}
                                placeholder="Ex.: cliente já pagou por PIX, comprovante anexado na fatura."
                                className={`${inputCls} resize-none`}
                            />
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                {alvo.etapas.length > 1
                                    ? 'As etapas saem da fila mas continuam no histórico, com o seu nome e o motivo.'
                                    : 'A etapa sai da fila mas continua no histórico, com o seu nome e o motivo.'}
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 p-5 border-t border-gray-100 dark:border-gray-700">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        Voltar
                    </button>
                    <button
                        onClick={confirmar}
                        disabled={!podeConfirmar || salvando}
                        className={`px-5 py-2.5 rounded-xl font-bold text-white transition-colors disabled:opacity-40 flex items-center gap-2 ${
                            registrando ? 'bg-primary hover:bg-primary/90' : 'bg-slate-600 hover:bg-slate-700'
                        }`}
                    >
                        {salvando
                            ? <Loader2 size={16} className="animate-spin" />
                            : registrando ? <CheckCircle2 size={16} /> : <Ban size={16} />}
                        {salvando ? 'Salvando...' : registrando ? (ehContato ? 'Confirmar contato' : 'Confirmar conferência') : 'Dispensar'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RegistrarAcaoModal;
